// @flow
import {logError} from './logError';
import type {Step, Extensions} from '../data/GuideTypes';

// Marks the start of a step {+ step-id}
export const START_STEP = /\{\+([a-zA-Z0-9-_]+?)\}/;
// Marks end of a step {/step-id}
export const END_STEP = /\{\/([a-zA-Z0-9-_]+?)\}/;
// An extension step -- lines will be hidden until extension enabled {? extension-id step-id}
export const EXTENSION_STEP = /\{\?([a-zA-Z0-9-_/]+?)\s([a-zA-Z0-9-_/]+?)\}/;
// Adds inline spans to elements with interactive behavior
// {span response step-id}response{/span}
// {span animate cardFont}#ffffff{/span}
export const SPAN = /\{span (response|animate) ([a-zA-Z0-9-_]+?)\}([#a-zA-Z0-9-_${},"'. ]+)\{\/span\}([a-zA-Z0-9-_=;,: ]+)?/;
// Marks the start of a tabbed-step (e.g. ElementsEditor) {+step-id substep}
export const TABBED_STEP = /\{\+([a-zA-Z0-9-_]+) ([a-zA-Z0-9-_]+)\}/;

type ParseFileMethod = {|
  rawFile: string,
  filename: string,
  stepsData: Array<Step>,
  extensionData: Extensions,
|};

/*
 * Takes raw File content and updates our list of Steps to mark start / end lines for each Step
 * Reads and strips away all our custom tags (e.g. {+ step-id substep}) so we can display the lines of code
 */
export const parseFile = ({
  rawFile,
  stepsData,
  filename,
  extensionData,
}: ParseFileMethod) => {
  const spans = [];

  // show keeps track of whether we want to show the lines
  // e.g. hide lines that are part of inactive extension
  let show = true;
  let offset = 0;
  let step;
  // keeps track of how many nested extensions we have, e.g.
  // {?option-1 a}
  //    {?option-2 b}
  //       var a = b;
  //    {/b}
  // {/a}
  // would build a list of ['option-1', 'option-2'] and track which extensions the line of code is affected by
  const extensions = [];

  const linesAdded = [];

  const tokens = rawFile.split('\n');

  // For each line, parse out any tags we have marking steps
  const lines = tokens.reduce<any>((list, token, i) => {
    try {
      if (token.match(SPAN)) {
        // e.g {span response step-id}variableName{/span}
        const data = token.match(SPAN);
        const line = token.replace(SPAN, '$3$4');

        if (data && data.length > 3) {
          spans.push({
            type: data[1],
            index: i - offset,
            value: data[2],
            content: data[3],
          });
        }

        if (show) {
          return [...list, line];
        }
      } else if (token.match(TABBED_STEP)) {
        // e.g {+step-id substep}
        const data = token.match(TABBED_STEP);
        if (data && data.length > 2) {
          const [, stepId, tabId] = data;
          step = stepsData.find((s) => s.id === stepId);
          if (step && step.tabs && step.tabs.active === tabId) {
            step.lines = {start: i + 1 - offset};
            step.file = filename;
          }
          if (step && step.tabs) {
            const tab = step.tabs.options[tabId];
            tab.lines = {start: i + 1 - offset};
            tab.file = filename;
          } else {
            logError(
              new Error(
                `Error: step ${String(step && step.id)} does not have tabs`,
              ),
            );
          }
          offset++;
          show = true;
        }
        return list;
      } else if (token.match(START_STEP)) {
        // e.g {+step-id}
        const start = token.match(START_STEP);

        if (start && start.length > 1) {
          step = stepsData.find((s) => s.id === start[1]);
          if (step) {
            step.lines = {start: i + 1 - offset};
            step.file = filename;
          }
          offset++;
          show = true;
        }
        return list;
      } else if (token.match(END_STEP)) {
        // e.g {/step-id}
        const endStep = token.match(END_STEP) || [];
        const stepId = endStep[1];

        if (stepId && stepId !== 'none') {
          step = stepsData.find((s) => s.id === stepId);
          if (step && step.tabs && step.tabs.options) {
            const stepOptions = step.tabs.options;
            const key = Object.keys(stepOptions).find(
              (k) => stepOptions[k] && stepOptions[k].file === filename,
            );
            if (key && stepOptions[key].lines && step.lines) {
              stepOptions[key].lines.end = i - offset;
              if (step.tabs && key === step.tabs.active) {
                step.lines.end = i - offset;
              }
            }
          } else if (step && step.lines) {
            step.lines.end = i - offset;
          }
        }

        if (!step || (step && step.extensionId)) {
          // If there is no step it means we came across an extension step that is not reflected in the current
          // narrative (e.g. save card after email is not an extension for iOS or Android steps but has steps in the shared server code)

          // If this is the end of an extension step (with an extensionId) then pop
          // off the last extension id we pushed to the queue
          extensions.pop();
        }

        if (extensions.length) {
          // Only show the next line of code if this end step is not nested in some inactive extensions
          show = extensions.reduce((shouldShow, extensionId) => {
            return extensionData[extensionId] && shouldShow;
          }, true);
        } else {
          show = true;
        }
        offset++;
        return list;
      } else if (token.match(EXTENSION_STEP)) {
        // e.g {?extension-id step-id}
        const option = token.match(EXTENSION_STEP);

        if (option && option.length > 2) {
          show = extensionData[option[1]];
          if (option[2] !== 'none') {
            step = stepsData.find((s) => s.id === option[2]);
            if (step) {
              step.lines = {start: i + 1 - offset};
              step.file = filename;
            }
          }
          offset++;
          extensions.push(option[1]);
        }
        return list;
      }
      if (!show) {
        offset++;
      }
    } catch (e) {
      logError(
        new Error(`Error when parsing ${filename} at token ${token}: ${e}`),
      );
    }

    if (extensions.length && show) {
      // Keep track of the lines that were linesAdded by the extension
      // so we can later animate them
      linesAdded.push(i - offset);
    }

    // Otherwise token is a plaintext line of code
    // Add it to the list if we are showing this line, otherwise hide it by not including
    return show ? [...list, token] : list;
  }, []);

  return {lines, spans, linesAdded};
};
